import { action, computed, set } from "@ember/object"
import Component from "@glimmer/component"
import { isApple } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { HIGHLIGHT_COLORS, TEXT_COLORS } from "cc-frontend/lib/tiptap/cc-tiptap"
import type { Instance } from "tippy.js"
import tippy from "tippy.js"
import type { FlatListType } from "tiptap-extension-flat-list"
import styles from "./tiptap-toolbar.module.css"

interface Sig {
  Args: {
    // Increment this on each transaction so that we know to re-read the editor state.
    trCounter: number
    editor: Editor
    // We pass these actions up to ScribeEditor so that it can manage its activeState.
    addImage: () => void
    addLink: () => void
    usedAs: "bubbleMenu" | "toolbar"
  }
}

export default class TiptapToolbar extends Component<Sig> {
  readonly styles = styles
  readonly modKey = isApple ? "⌘" : "Ctrl"
  readonly highlightColors = HIGHLIGHT_COLORS

  readonly textColors = TEXT_COLORS

  highlightMenuIsOpen = false
  highlightButtonElement!: HTMLElement
  highlightTippy: Instance | null = null

  textColorMenuIsOpen = false
  textColorButtonElement!: HTMLElement
  textColorTippy: Instance | null = null

  // Button states to render based on the current editor state and selection.

  @computed("args.trCounter")
  get boldState() {
    return this.getBooleanMarkState("bold")
  }

  @computed("args.trCounter")
  get italicState() {
    return this.getBooleanMarkState("italic")
  }

  @computed("args.trCounter")
  get underlineState() {
    return this.getBooleanMarkState("underline")
  }

  @computed("args.trCounter")
  get strikeState() {
    return this.getBooleanMarkState("strike")
  }

  @computed("args.trCounter")
  get subscriptState() {
    return this.getBooleanMarkState("subscript")
  }

  @computed("args.trCounter")
  get superscriptState() {
    return this.getBooleanMarkState("superscript")
  }

  private getBooleanMarkState(markType: string) {
    const editor = this.args.editor
    return editor.can().toggleMark(markType)
      ? editor.isActive(markType)
        ? "active"
        : "enabled"
      : "disabled"
  }

  @computed("args.trCounter", "highlightMenuIsOpen")
  get highlightMenuState() {
    if (this.highlightMenuIsOpen) return "open"
    else return this.args.editor.can().setHighlight() ? "closed" : "disabled"
  }

  @computed("args.trCounter")
  get highlightColor() {
    const editor = this.args.editor
    // Check if a highlight is active *anywhere* in the selection.
    const color = editor.getAttributes("highlight")?.color
    if (color === undefined) return "inherit"

    // Now check if it's the same color everywhere.
    if (editor.isActive("highlight", { color })) return color
    else return ""
  }

  @computed("args.trCounter", "textColorMenuIsOpen")
  get textColorMenuState() {
    if (this.textColorMenuIsOpen) return "open"
    else return this.args.editor.can().setColor("#000000") ? "closed" : "disabled"
  }

  @computed("args.trCounter")
  get textColor() {
    const editor = this.args.editor
    // Check if a text color is active *anywhere* in the selection.
    const color = editor.getAttributes("textStyle")?.color
    if (color === undefined) return "#212121"

    // Now check if it's the same color everywhere.
    if (editor.isActive("textStyle", { color })) return color
    else return ""
  }

  @computed("args.trCounter")
  get orderedListState() {
    return this.getListState("flatListItemOrdered")
  }

  @computed("args.trCounter")
  get taskListState() {
    return this.getListState("flatListItemTask")
  }

  @computed("args.trCounter")
  get unorderedListState() {
    return this.getListState("flatListItemUnordered")
  }

  private getListState(listType: FlatListType) {
    const editor = this.args.editor
    return editor.can().toggleFlatListItem(listType)
      ? editor.isActive(listType)
        ? "active"
        : "enabled"
      : "disabled"
  }

  @computed("args.trCounter")
  get imageState() {
    return this.args.editor.can().insertContent({ type: Image.name, attrs: { src: "" } })
      ? "enabled"
      : "disabled"
  }

  @computed("args.trCounter")
  get linkState() {
    return this.args.editor.can().setLink({ href: "" }) ? "enabled" : "disabled"
  }

  @computed("args.trCounter")
  get unlinkState() {
    // Check if a link is active *anywhere* in the selection.
    return this.args.editor.getAttributes("link")?.href !== undefined ? "enabled" : "disabled"
  }

  // Toolbar actions

  @action
  toggleBooleanMark(markType: string) {
    this.args.editor.chain().focus().toggleMark(markType).run()
  }

  @action
  toggleList(listType: FlatListType) {
    this.args.editor.chain().focus().toggleFlatListItem(listType).run()
  }

  @action
  unlink() {
    this.args.editor.chain().focus().unsetLink().run()
  }

  @action
  toggleHighlightMenuIsOpen() {
    set(this, "highlightMenuIsOpen", !this.highlightMenuIsOpen)
    if (this.highlightMenuIsOpen) this.highlightTippy!.show()
    else this.highlightTippy!.hide()
  }

  @action
  setHighlightColor(color: string) {
    this.args.editor.chain().focus().setHighlight({ color }).run()
  }

  @action
  setHighlightNone() {
    this.args.editor.chain().focus().unsetHighlight().run()
  }

  @action
  toggleTextColorMenuIsOpen() {
    set(this, "textColorMenuIsOpen", !this.textColorMenuIsOpen)
    if (this.textColorMenuIsOpen) this.textColorTippy!.show()
    else this.textColorTippy!.hide()
  }

  @action
  setTextColor(color: string) {
    this.args.editor.chain().focus().setColor(color).run()
  }

  @action
  setTextColorNone() {
    this.args.editor.chain().focus().unsetColor().run()
  }

  // Misc actions

  @action
  onMouseDown(e: Event) {
    switch (this.args.usedAs) {
      case "bubbleMenu":
        // The BubbleMenu extension expects the editor to lose and regain focus every time the
        // user interacts with it. Specifically, it waits for the editor to blur *twice*
        // before hiding (once due to this event and once when the editor actually loses focus).
        setTimeout(() => this.args.editor.commands.focus())
        break
      case "toolbar":
        // Prevent the toolbar from being focused, which causes it to flash
        // (since the editor briefly loses focus).
        e.preventDefault()
        break
    }
  }

  @action
  storeHighlightButton(element: HTMLElement) {
    this.highlightButtonElement = element
  }

  @action
  didInsertHighlightMenu(element: HTMLDivElement) {
    this.highlightTippy = tippy(this.highlightButtonElement, {
      content: element,
      interactive: true,
      trigger: "manual",
      hideOnClick: false,
      onClickOutside: (instance, event) => {
        // Hide when clicking outside the element, unless the user clicked on the trigger button
        // (wait for its own click handler to fire).
        if (event.target instanceof Node && this.highlightButtonElement.contains(event.target)) {
          return
        }
        instance.hide()
      },
      onHide: () => {
        // Besides when we explicitly hide the menu, this is called when the toolbar itself is hidden.
        // Ensure the button reflects the closed menu.
        set(this, "highlightMenuIsOpen", false)
      },
      placement: "top",
      duration: 100,
    })
  }

  @action
  storeTextColorButton(element: HTMLElement) {
    this.textColorButtonElement = element
  }

  @action
  didInsertTextColorMenu(element: HTMLDivElement) {
    this.textColorTippy = tippy(this.textColorButtonElement, {
      content: element,
      interactive: true,
      trigger: "manual",
      hideOnClick: false,
      onClickOutside: (instance, event) => {
        // Hide when clicking outside the element, unless the user clicked on the trigger button
        // (wait for its own click handler to fire).
        if (event.target instanceof Node && this.textColorButtonElement.contains(event.target)) {
          return
        }
        instance.hide()
      },
      onHide: () => {
        // Besides when we explicitly hide the menu, this is called when the toolbar itself is hidden.
        // Ensure the button reflects the closed menu.
        set(this, "textColorMenuIsOpen", false)
      },
      placement: "top",
      duration: 100,
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Editor::TiptapToolbar": typeof TiptapToolbar
  }
}
